import React, { useEffect, useRef, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import { Container, Content, Drawer } from "rsuite";
import { AuthContext } from "../Main.js";
import { getEnvStyle } from "../EnvBanner.js";
import { NAV_ENABLED_APPS } from "../../../config/config.js";
import { EraIcon } from "../../../styles/Icon.js";
import { getUserInitials } from "../../../utils/getUserInitials";
import RedesignHeader from "./header/RedesignHeader";
import RedesignSidebar, { RedesignNavTree } from "./sidebar/RedesignSidebar";
import RedesignFooter from "./footer/RedesignFooter";
import { NARROW_VIEWPORT_QUERY } from "./breakpoints";
import "./RedesignShell.css";
const NAV_DRAWER_ID = "redesign-shell-nav-drawer";
/*
 * Same fields UserMenu reads for the header label. The drawer only shows the
 * active organisation: the represented IM for an NRE, otherwise the user's
 * own org. The header receives this same activeRole / company copy.
 */
function representedOrganisation(activeRole, company) {
    if (!activeRole?.role || company === null || typeof company !== "object") {
        return null;
    }
    const name = activeRole.role === "nre"
        ? company.im?.find((im) => im?.id === activeRole.company)?.name
        : company.org?.name;
    return name ? name : null;
}
export default function RedesignShell({ children, }) {
    const [enabledComponents, setEnabledComponents] = useState([]);
    const [enabledExtras, setEnabledExtras] = useState([]);
    const [isAuthenticated, setIsAuthenticated] = useState();
    const [userInfo, setUserInfo] = useState();
    const [userRoles, setUserRoles] = useState();
    const [activeRole, setActiveRole] = useState();
    const [userRolesCompany, setUserRolesCompany] = useState();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const shellRef = useRef(null);
    const location = useLocation();
    const { auth, authInfo, authRoles } = useContext(AuthContext);
    useEffect(() => {
        if (auth) {
            setUserInfo(authInfo);
            setUserRoles(authRoles?.available);
            setUserRolesCompany(authRoles?.company);
            setActiveRole(authRoles?.active);
            setIsAuthenticated(true);
        }
        else {
            setIsAuthenticated(false);
            setUserInfo(undefined);
            setUserRoles(undefined);
            setActiveRole(undefined);
        }
    }, [auth, authInfo, authRoles]);
    useEffect(() => {
        try {
            let enabledConfig = JSON.parse(atob(NAV_ENABLED_APPS));
            setEnabledComponents(enabledConfig.components);
            setEnabledExtras(enabledConfig.extras);
        }
        catch (error) {
            setEnabledComponents([
                "era-search",
                "unified-era-search",
                "era-compatibility-check",
                "era-dataset-explorer",
                "era-notifications",
                "era-dataset-manager",
                "era-endpoint",
                "era-datastories",
                "era-vocabulary",
            ]);
            setEnabledExtras(["userLogin"]);
        }
    }, []);
    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);
    useEffect(() => {
        const narrow = window.matchMedia(NARROW_VIEWPORT_QUERY);
        const closeWhenWide = (event) => {
            if (!event.matches) {
                setDrawerOpen(false);
            }
        };
        narrow.addEventListener("change", closeWhenWide);
        return () => narrow.removeEventListener("change", closeWhenWide);
    }, []);
    /*
     * VoiceOver ignores the Drawer's aria-modal, so the page behind it has to be
     * made inert. The Drawer portals to document.body and stays interactive.
     * React 18 drops an `inert` JSX prop, hence the DOM attribute. It must be
     * cleared as soon as drawerOpen is false: RSuite returns focus to the
     * hamburger (inside this root) only after the exit transition.
     */
    useEffect(() => {
        shellRef.current?.toggleAttribute("inert", drawerOpen);
    }, [drawerOpen]);
    /*
     * Read the display name off the context rather than the local userInfo copy:
     * the copy is written by the effect above, so on the first paint after a
     * sign-in it is still undefined and the badge would flicker in.
     */
    const userInitials = getUserInitials(authInfo?.displayName);
    const envStyle = getEnvStyle();
    const drawerRole = auth && activeRole?.role ? activeRole.role : null;
    const drawerOrganisation = drawerRole
        ? representedOrganisation(activeRole, userRolesCompany)
        : null;
    return (React.createElement(Container, { ref: shellRef, className: "redesign-shell" },
        React.createElement(RedesignHeader, { auth: auth, userInitials: userInitials, activeRole: activeRole, enabledExtras: enabledExtras, isAuthenticated: isAuthenticated, userInfo: userInfo, userRoles: userRoles, userRolesCompany: userRolesCompany, setRole: authRoles?.set, menuButton: React.createElement("button", { type: "button", className: "redesign-shell-menu-button", "aria-label": "Open menu", "aria-expanded": drawerOpen, "aria-controls": NAV_DRAWER_ID, onClick: () => setDrawerOpen(true) },
                React.createElement(EraIcon, { faName: "bars", style: { margin: "var(--era-space-0)" } })) }),
        React.createElement(Container, { className: "redesign-shell-body" },
            React.createElement(RedesignSidebar, { enabledComponents: enabledComponents, activeRole: activeRole }),
            React.createElement(Content, { className: "redesign-shell-content" }, children)),
        React.createElement(RedesignFooter, null),
        React.createElement(Drawer, { id: NAV_DRAWER_ID, className: "redesign-shell-drawer", placement: "left", open: drawerOpen, onClose: () => setDrawerOpen(false) },
            React.createElement(Drawer.Header, { closeButton: false },
                drawerRole ? (React.createElement("div", { className: "redesign-shell-drawer-heading" },
                    React.createElement(Drawer.Title, null, "Menu"),
                    React.createElement("p", { className: "redesign-shell-drawer-account" },
                        React.createElement("span", { className: "redesign-shell-drawer-role" }, drawerRole.toUpperCase()),
                        drawerOrganisation ? (React.createElement("span", { className: "redesign-shell-drawer-org", title: drawerOrganisation }, drawerOrganisation)) : null))) : (React.createElement(Drawer.Title, null, "Menu")),
                envStyle && (React.createElement("span", { className: "redesign-shell-env-badge", style: { backgroundColor: envStyle.bg, color: envStyle.fg } }, envStyle.label)),
                React.createElement("button", { type: "button", className: "redesign-shell-drawer-close", "aria-label": "Close menu", onClick: () => setDrawerOpen(false) },
                    React.createElement(EraIcon, { faName: "xmark", style: { margin: "var(--era-space-0)" } }))),
            React.createElement(Drawer.Body, { className: "redesign-shell-sidebar", onClick: (event) => {
                    if (event.target.closest("a")) {
                        setDrawerOpen(false);
                    }
                } },
                React.createElement(RedesignNavTree, { expanded: true, enabledComponents: enabledComponents, activeRole: activeRole })))));
}
